import type {
  IBoundarySchoolAssignment,
  IBoundarySchoolIdentity,
  IBoundarySchoolLink,
} from "../types/boundary-school-link.types";

export type {
  IBoundarySchoolAssignment,
  IBoundarySchoolIdentity,
  IBoundarySchoolLink,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value != null && !Array.isArray(value);
}

function parseLink(value: unknown): IBoundarySchoolLink | null {
  if (!isRecord(value)) {
    return null;
  }

  const schoolInternalId =
    typeof value.schoolInternalId === "string" ? value.schoolInternalId.trim() : "";
  if (!schoolInternalId) {
    return null;
  }

  return {
    schoolId: typeof value.schoolId === "string" ? value.schoolId : null,
    schoolInternalId,
    schoolName: typeof value.schoolName === "string" ? value.schoolName : null,
  };
}

export function parseBoundarySchoolLinks(value: unknown): IBoundarySchoolLink[] {
  let raw: unknown = value;

  if (typeof value === "string") {
    try {
      raw = JSON.parse(value) as unknown;
    } catch {
      return [];
    }
  }

  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.flatMap((item) => {
    const link = parseLink(item);
    return link ? [link] : [];
  });
}

export function dedupeBoundarySchoolLinks(
  links: readonly IBoundarySchoolLink[],
): IBoundarySchoolLink[] {
  const seen = new Set<string>();
  const deduped: IBoundarySchoolLink[] = [];

  for (const link of links) {
    const parsed = parseLink(link);
    if (!parsed || seen.has(parsed.schoolInternalId)) {
      continue;
    }

    seen.add(parsed.schoolInternalId);
    deduped.push(parsed);
  }

  return deduped;
}

export function compareSchoolInternalIdAsc(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}

export function primaryBoundarySchoolLink(
  links: readonly IBoundarySchoolLink[] | null | undefined,
): IBoundarySchoolLink | null {
  const [primary] = dedupeBoundarySchoolLinks(links ?? []);
  return primary ?? null;
}

export function primaryBoundarySchoolIdentity(
  links: readonly IBoundarySchoolLink[] | null | undefined,
): IBoundarySchoolIdentity {
  const primary = primaryBoundarySchoolLink(links);

  return {
    schoolId: primary?.schoolId ?? null,
    schoolInternalId: primary?.schoolInternalId ?? "",
    schoolName: primary?.schoolName ?? null,
  };
}

export function toBoundarySchoolLinksFromSchool(
  school: IBoundarySchoolAssignment | null,
): IBoundarySchoolLink[] {
  const internalId = school?.internalId?.trim();
  if (!internalId) {
    return [];
  }

  return [
    {
      schoolId: school?.id ?? null,
      schoolInternalId: internalId,
      schoolName: school?.name ?? null,
    },
  ];
}

function readNestedFeatureProperties(
  props: Record<string, unknown>,
): Record<string, unknown> | null {
  const nested = props.properties;

  if (typeof nested === "string") {
    try {
      const parsed = JSON.parse(nested) as unknown;
      return isRecord(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  return isRecord(nested) ? nested : null;
}

function resolveSchoolsPayloadFromFeatureProperties(
  props: Record<string, unknown>,
): { present: boolean; value: unknown } {
  if ("schools" in props) {
    return { present: true, value: props.schools };
  }

  const nested = readNestedFeatureProperties(props);
  if (nested && "schools" in nested) {
    return { present: true, value: nested.schools };
  }

  return { present: false, value: undefined };
}

export function boundarySchoolLinksFromFeatureProperties(
  props: Record<string, unknown> | undefined,
): IBoundarySchoolLink[] {
  if (!props) {
    return [];
  }

  const schoolsPayload = resolveSchoolsPayloadFromFeatureProperties(props);
  if (schoolsPayload.present) {
    return dedupeBoundarySchoolLinks(parseBoundarySchoolLinks(schoolsPayload.value));
  }

  const nested = readNestedFeatureProperties(props);
  const legacySource = nested ?? props;

  return toBoundarySchoolLinksFromSchool({
    id: typeof legacySource.schoolId === "string" ? legacySource.schoolId : null,
    internalId:
      typeof legacySource.schoolInternalId === "string" ? legacySource.schoolInternalId : "",
    name: typeof legacySource.schoolName === "string" ? legacySource.schoolName : null,
  });
}

export function primaryBoundarySchoolIdentityFromFeatureProperties(
  props: Record<string, unknown> | undefined,
): IBoundarySchoolIdentity {
  return primaryBoundarySchoolIdentity(boundarySchoolLinksFromFeatureProperties(props));
}

export function isBoundaryAssignedFromFeatureProperties(
  props: Record<string, unknown> | undefined,
): boolean {
  return boundarySchoolLinksFromFeatureProperties(props).length > 0;
}

function boundarySchoolLinkMatchKeys(
  links: readonly IBoundarySchoolLink[],
): Set<string> {
  const keys = new Set<string>();

  for (const link of dedupeBoundarySchoolLinks(links)) {
    keys.add(`i:${link.schoolInternalId}`);
    const schoolId = link.schoolId?.trim();
    if (schoolId) {
      keys.add(`u:${schoolId}`);
    }
  }

  return keys;
}

/** True when the two link sets share any schoolInternalId or schoolId. */
export function boundarySchoolLinksShareAny(
  left: readonly IBoundarySchoolLink[] | null | undefined,
  right: readonly IBoundarySchoolLink[] | null | undefined,
): boolean {
  const leftKeys = boundarySchoolLinkMatchKeys(left ?? []);
  if (leftKeys.size === 0) {
    return false;
  }

  for (const key of boundarySchoolLinkMatchKeys(right ?? [])) {
    if (leftKeys.has(key)) {
      return true;
    }
  }

  return false;
}

export function boundarySchoolsWriteFromFeatureProperties(
  props: Record<string, unknown> | undefined,
): IBoundarySchoolLink[] | undefined {
  if (!props) {
    return undefined;
  }

  if ("schools" in props) {
    return dedupeBoundarySchoolLinks(parseBoundarySchoolLinks(props.schools));
  }

  if (!("schoolInternalId" in props)) {
    return undefined;
  }

  return toBoundarySchoolLinksFromSchool({
    id: typeof props.schoolId === "string" ? props.schoolId : null,
    internalId: typeof props.schoolInternalId === "string" ? props.schoolInternalId : "",
    name: typeof props.schoolName === "string" ? props.schoolName : null,
  });
}
